import type { AppRecord } from '../../lib/types';
import type { CompassLocale } from '../i18n';
import { buildCompassHref } from '../i18n';
import { deriveAppCategory, isDemoAvailable } from '../lib/catalogPresentation';

export function DemosView(props: {
  apps: AppRecord[];
  locale: CompassLocale;
}): React.JSX.Element {
  const { apps, locale } = props;
  const text = locale === 'zh-CN'
    ? {
        title: '应用演示',
        description: '优先查看有可用预览、部署包或运行态能力的应用，快速验证成品效果。',
        tryDemo: '打开 Demo',
        details: '查看详情',
        noDemo: '暂无可直接打开的 Demo，将跳转到详情页。',
      }
    : {
        title: 'App Demos',
        description: 'Prioritize apps with previews, deploy bundles, or live runtime signals to validate final output quickly.',
        tryDemo: 'Open Demo',
        details: 'View Details',
        noDemo: 'No direct demo is available yet; the detail view is the next best route.',
      };

  const demoApps = apps.filter((app) => isDemoAvailable(app) || app.screenshots.length > 0);

  return (
    <main className="flex-grow max-w-[1280px] mx-auto w-full px-4 md:px-10 py-12 pt-[100px] flex flex-col gap-12">
      <header className="max-w-3xl">
        <h1 className="font-geist text-[48px] leading-[1.1] font-bold text-on-surface mb-4">{text.title}</h1>
        <p className="text-lg leading-relaxed text-on-surface-variant">{text.description}</p>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {demoApps.map((app) => {
          const previewHref = app.preview.url || buildCompassHref(`/apps/${app.appId}`, locale);
          const detailHref = buildCompassHref(`/apps/${app.appId}`, locale);
          return (
            <article key={app.appId} className="glass-card rounded-2xl overflow-hidden flex flex-col">
              <div className="relative aspect-[16/10] bg-surface-container">
                {app.screenshots[0]?.publishedUrl ? (
                  <img alt={app.name} className="w-full h-full object-cover opacity-60" src={app.screenshots[0].publishedUrl} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-outline-variant">
                    <span className="material-symbols-outlined text-6xl">play_circle</span>
                  </div>
                )}
                <div className="absolute top-4 left-4 rounded-full border border-cyan/40 bg-surface-container-highest/80 px-3 py-1 text-xs uppercase tracking-widest text-cyan">
                  {deriveAppCategory(app)}
                </div>
              </div>

              <div className="p-6 flex flex-col gap-4 flex-grow">
                <div>
                  <h3 className="font-geist text-2xl font-semibold text-on-surface mb-2">{app.name}</h3>
                  <p className="text-sm leading-relaxed text-on-surface-variant line-clamp-5">{app.summary}</p>
                </div>
                <p className="text-xs text-outline-variant">{isDemoAvailable(app) ? app.appId : text.noDemo}</p>
                <div className="mt-auto flex gap-3 pt-3 border-t border-outline-variant/20">
                  <a
                    className="btn-primary-gradient flex-1 rounded-full py-3 text-center text-xs uppercase tracking-widest no-underline"
                    href={previewHref}
                    target={app.preview.url ? '_blank' : undefined}
                  >
                    {text.tryDemo}
                  </a>
                  <a
                    className="btn-secondary-cyan flex-1 rounded-full py-3 text-center text-xs uppercase tracking-widest no-underline"
                    href={detailHref}
                  >
                    {text.details}
                  </a>
                </div>
              </div>
            </article>
          );
        })}
      </section>
    </main>
  );
}
